import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, CircularProgress, Paper, Link,
  FormControl, Select, MenuItem, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, useMediaQuery, useTheme
} from '@mui/material';
import {
  TrendingUp, TrendingDown, Search, Euro, Payments, AccountBalance,
  Percent, SwapHoriz, AdsClick, People, CheckCircle, HourglassFull, Delete, Star
} from '@mui/icons-material';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

function Dashboard() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [chartMetrics, setChartMetrics] = useState(['revenue']);
  const [timeRange, setTimeRange] = useState('today');
  const [campaignStats, setCampaignStats] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [campaignHourly, setCampaignHourly] = useState({ today: [], yesterday: [] });
  const [partnerStats, setPartnerStats] = useState([]);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [partnerHourly, setPartnerHourly] = useState({ today: [], yesterday: [] });
  const { user, isPartner, isAdmin } = useAuth();
  const userName = user?.full_name || user?.name || 'User';
  const [campaignSearch, setCampaignSearch] = useState('');
  const [partnerSearch, setPartnerSearch] = useState('');
  const [fbStats, setFbStats] = useState(null);

  useEffect(() => {
    fetchData();
    fetchCampaignStats();
    fetchPartnerStats();
    if (isAdmin()) fetchFbStats();
  }, []);

  const fetchData = async () => {
    try {
      const response = await axios.get('/admin/stats');
      setStats(response.data.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFbStats = async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const response = await axios.get(`/admin/facebook/dashboard/stats?start_date=${today}&end_date=${today}`);
      if (response.data.success) {
        setFbStats(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching FB stats:', error);
    }
  };

  const fetchCampaignHourly = async (campaignId) => {
    try {
      const response = await axios.get(`/admin/stats/campaigns/${campaignId}/hourly`);
      setCampaignHourly(response.data.data || { today: [], yesterday: [] });
    } catch (error) {
      console.error('Error fetching campaign hourly:', error);
    }
  };

  const fetchCampaignStats = async () => {
    try {
      const response = await axios.get('/admin/stats/campaigns');
      setCampaignStats(response.data.data || []);
      if (response.data.data && response.data.data.length > 0) {
        const bestCampaign = response.data.data[0];
        setSelectedCampaign(bestCampaign);
        fetchCampaignHourly(bestCampaign.campaign_id);
      }
    } catch (error) {
      console.error('Error fetching campaign stats:', error);
    }
  };

  const fetchPartnerHourly = async (partnerId) => {
    try {
      const response = await axios.get(`/admin/stats/partners/${partnerId}/hourly`);
      setPartnerHourly(response.data.data || { today: [], yesterday: [] });
    } catch (error) {
      console.error('Error fetching partner hourly:', error);
    }
  };

  const fetchPartnerStats = async () => {
    try {
      const response = await axios.get('/admin/stats/partners');
      setPartnerStats(response.data.data || []);
      if (response.data.data && response.data.data.length > 0) {
        const bestPartner = response.data.data[0];
        setSelectedPartner(bestPartner);
        fetchPartnerHourly(bestPartner.partner_id);
      }
    } catch (error) {
      console.error('Error fetching partner stats:', error);
    }
  };

  const handleCampaignClick = (campaign) => {
    setSelectedCampaign(campaign);
    fetchCampaignHourly(campaign.campaign_id);
  };

  const handlePartnerClick = (partner) => {
    setSelectedPartner(partner);
    fetchPartnerHourly(partner.partner_id);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  // Prepare main chart data with cumulative values
  const prepareChartData = () => {
    if (timeRange === 'today' || timeRange === 'lastWeekday') {
      const hours = Array.from({ length: 24 }, (_, i) => ({
        label: `${i.toString().padStart(2, '0')}:00`,
        revenue: 0, profit: 0, revenuePrev: 0, profitPrev: 0,
      }));
      
     // Fill in today's data
      const valueField = isPartner() ? 'payout' : 'revenue';
      if (stats?.hourly) {
        stats.hourly.forEach(h => {
          const hourIndex = parseInt(h.hour);
          if (hourIndex >= 0 && hourIndex < 24) {
            hours[hourIndex].revenue = parseFloat(h[valueField]) || 0;
            hours[hourIndex].profit = (parseFloat(h.revenue) || 0) - (parseFloat(h.payout) || 0);
          }
        });
      }
      
      // Fill in previous data
      const prevData = timeRange === 'today' ? stats?.hourlyYesterday : stats?.hourlyLastWeekday;
      if (prevData) {
        prevData.forEach(h => {
          const hourIndex = parseInt(h.hour);
          if (hourIndex >= 0 && hourIndex < 24) {
            hours[hourIndex].revenuePrev = parseFloat(h[valueField]) || 0;
            hours[hourIndex].profitPrev = (parseFloat(h.revenue) || 0) - (parseFloat(h.payout) || 0);
          }
        });
      }
      
      return hours;
    } else if (timeRange === 'week') {
      const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
      let cumRev = 0, cumProfit = 0, cumRevPrev = 0, cumProfitPrev = 0;
      return days.map((day, i) => {
        const rev = parseFloat(stats?.weekDaily?.[i]?.revenue) || 0;
        const pay = parseFloat(stats?.weekDaily?.[i]?.payout) || 0;
        const revPrev = parseFloat(stats?.lastWeekDaily?.[i]?.revenue) || 0;
        const payPrev = parseFloat(stats?.lastWeekDaily?.[i]?.payout) || 0;
        cumRev += rev;
        cumProfit += rev - pay;
        cumRevPrev += revPrev;
        cumProfitPrev += revPrev - payPrev;
        return { label: day, revenue: cumRev, profit: cumProfit, revenuePrev: cumRevPrev, profitPrev: cumProfitPrev };
      });
    } else if (timeRange === 'month') {
      const daysInMonth = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).getDate();
      let cumRev = 0, cumProfit = 0, cumRevPrev = 0, cumProfitPrev = 0;
      return Array.from({ length: daysInMonth }, (_, i) => {
        const rev = parseFloat(stats?.monthDaily?.[i]?.revenue) || 0;
        const pay = parseFloat(stats?.monthDaily?.[i]?.payout) || 0;
        const revPrev = parseFloat(stats?.lastMonthDaily?.[i]?.revenue) || 0;
        const payPrev = parseFloat(stats?.lastMonthDaily?.[i]?.payout) || 0;
        cumRev += rev;
        cumProfit += rev - pay;
        cumRevPrev += revPrev;
        cumProfitPrev += revPrev - payPrev;
        return { label: (i + 1).toString().padStart(2, '0'), revenue: cumRev, profit: cumProfit, revenuePrev: cumRevPrev, profitPrev: cumProfitPrev };
      });
    }
    return [];
  };

  // Prepare campaign chart data with cumulative values
  const prepareCampaignChartData = () => {
    const hours = Array.from({ length: 24 }, (_, i) => ({ 
      label: `${i.toString().padStart(2, '0')}:00`, 
      revenue: 0, 
      revenuePrev: 0 
    }));
    
    const valueField = isPartner() ? 'payout' : 'revenue';
    
    if (campaignHourly.today && campaignHourly.today.length > 0) {
      campaignHourly.today.forEach(h => { 
        const idx = parseInt(h.hour);
        if (idx >= 0 && idx < 24) {
        hours[idx].revenue = parseFloat(h[valueField]) || 0;
        }
      });
    }
    
    if (campaignHourly.yesterday && campaignHourly.yesterday.length > 0) {
      campaignHourly.yesterday.forEach(h => { 
        const idx = parseInt(h.hour);
        if (idx >= 0 && idx < 24) {
        hours[idx].revenuePrev = parseFloat(h[valueField]) || 0;
        }
      });
    }
    
    
    return hours;
  };

  // Prepare partner chart data with cumulative values
  const preparePartnerChartData = () => {
    const hours = Array.from({ length: 24 }, (_, i) => ({ 
      label: `${i.toString().padStart(2, '0')}:00`, 
      revenue: 0, 
      revenuePrev: 0 
    }));
    
    if (partnerHourly.today && partnerHourly.today.length > 0) {
      partnerHourly.today.forEach(h => { 
        const idx = parseInt(h.hour);
        if (idx >= 0 && idx < 24) {
          hours[idx].revenue = parseFloat(h.revenue) || 0;
        }
      });
    }
    
    if (partnerHourly.yesterday && partnerHourly.yesterday.length > 0) {
      partnerHourly.yesterday.forEach(h => { 
        const idx = parseInt(h.hour);
        if (idx >= 0 && idx < 24) {
          hours[idx].revenuePrev = parseFloat(h.revenue) || 0;
        }
      });
    }
    
    
    return hours;
  };

  const chartData = prepareChartData();
  const campaignChartData = prepareCampaignChartData();
  const partnerChartData = preparePartnerChartData();

  const getComparisonData = () => {
    switch (timeRange) {
      case 'today': return { current: stats?.today, previous: stats?.yesterday, currentLabel: 'Today', previousLabel: 'Yesterday' };
      case 'lastWeekday': return { current: stats?.today, previous: stats?.lastSameWeekday, currentLabel: 'Today', previousLabel: `Last ${new Date().toLocaleDateString('en-US', { weekday: 'long' })}` };
      case 'week': return { current: stats?.currentWeek, previous: stats?.lastWeek, currentLabel: 'This Week', previousLabel: 'Last Week' };
      case 'month': return { current: stats?.month, previous: stats?.lastMonth, currentLabel: 'This Month', previousLabel: 'Last Month' };
      default: return { current: stats?.today, previous: stats?.yesterday, currentLabel: 'Today', previousLabel: 'Yesterday' };
    }
  };

  const comparison = getComparisonData();

  const calcChange = (today, yesterday) => {
    if (!yesterday || yesterday === 0) return today > 0 ? 100 : 0;
    return ((today - yesterday) / yesterday * 100).toFixed(0);
  };

  const revenueChange = calcChange(stats?.today?.revenue, stats?.yesterday?.revenue);
  const payoutChange = calcChange(stats?.today?.payout, stats?.yesterday?.payout);
  const profitChange = calcChange(stats?.today?.profit, stats?.yesterday?.profit);
  const conversionsChange = calcChange(stats?.today?.conversions, stats?.yesterday?.conversions);
  const clicksChange = calcChange(stats?.today?.clicks, stats?.yesterday?.clicks);

  const marginToday = stats?.today?.revenue > 0 ? ((stats?.today?.profit / stats?.today?.revenue) * 100).toFixed(2) : '0.00';
  const marginMonth = stats?.month?.revenue > 0 ? ((stats?.month?.profit / stats?.month?.revenue) * 100).toFixed(2) : '0.00';
  const marginYesterday = stats?.yesterday?.revenue > 0 ? ((stats?.yesterday?.profit / stats?.yesterday?.revenue) * 100) : 0;
  const marginChange = String(Math.round(parseFloat(marginToday) - (marginYesterday || 0)));

  const formatNumber = (num, decimals = true) => {
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    if (!decimals || Number.isInteger(num)) return num || '0';
    return num?.toFixed?.(2) || num || '0';
  };

  // Custom tooltip that shows cumulative values from the data array
  const renderTooltip = (data, currentLabel, previousLabel) => ({ active, label }) => {
    if (!active || !data) return null;
    const point = data.find(d => d.label === label);
    if (!point) return null;
    
    return (
      <div style={{ backgroundColor: 'white', padding: '10px', border: '1px solid #ccc', borderRadius: '4px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
        <p style={{ margin: 0, fontWeight: 'bold', marginBottom: '5px' }}>{label}</p>
        {point.revenue > 0 && (
  <p style={{ margin: '3px 0', color: '#3b82f6', fontSize: '13px' }}>
    {isPartner() ? 'Payout' : 'Revenue'} {currentLabel}: <strong>€{point.revenue.toFixed(2)}</strong>
  </p>
)}
{point.revenuePrev > 0 && (
  <p style={{ margin: '3px 0', color: '#93c5fd', fontSize: '13px' }}>
    {isPartner() ? 'Payout' : 'Revenue'} {previousLabel}: <strong>€{point.revenuePrev.toFixed(2)}</strong>
  </p>
)}
  {!isPartner() && point.profit !== undefined && point.profit > 0 && (
  <p style={{ margin: '3px 0', color: '#10b981', fontSize: '13px' }}>
    Profit {currentLabel}: <strong>€{point.profit.toFixed(2)}</strong>
  </p>
)}
{!isPartner() && point.profitPrev !== undefined && point.profitPrev > 0 && (
  <p style={{ margin: '3px 0', color: '#6ee7b7', fontSize: '13px' }}>
    Profit {previousLabel}: <strong>€{point.profitPrev.toFixed(2)}</strong>
  </p>
)}
        {point.revenue === 0 && point.revenuePrev === 0 && (
          <p style={{ margin: '3px 0', color: '#999', fontSize: '13px' }}>No data</p>
        )}
      </div>
    );
  };

  const StatCard = ({ title, value, change, icon, iconBgColor, iconColor, prefix = '', suffix = '', subValue, integer = false }) => (
    <Card sx={{ height: '100%' }}>
      <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box flex={1}>
            <Typography variant="body2" color="text.secondary" fontWeight={500} sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' }, mb: 0.5 }}>
              {title}
            </Typography>
            <Typography variant="h5" fontWeight={700} sx={{ fontSize: { xs: '1.1rem', sm: '1.4rem' }, color: '#1e293b' }}>
              {prefix}{typeof value === 'number' ? formatNumber(value, !integer) : value}{suffix}
            </Typography>
            {change !== undefined && (
              <Box display="flex" alignItems="center" gap={0.5} mt={0.5}>
                {parseFloat(change) >= 0 ? (
                  <TrendingUp sx={{ color: '#10b981', fontSize: 12 }} />
                ) : (
                  <TrendingDown sx={{ color: '#ef4444', fontSize: 12 }} />
                )}
                <Typography variant="caption" sx={{ color: parseFloat(change) >= 0 ? '#10b981' : '#ef4444', fontWeight: 600, fontSize: '0.65rem' }}>
                  {Math.abs(parseFloat(change))}% vs ieri
                </Typography>
              </Box>
            )}
            {subValue && (
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem', display: 'block', mt: 0.25 }}>
                {subValue}
              </Typography>
            )}
          </Box>
          <Box sx={{ width: 32, height: 32, borderRadius: 1.5, bgcolor: iconBgColor, display: { xs: 'none', sm: 'flex' }, alignItems: 'center', justifyContent: 'center', ml: 1 }}>
            {React.cloneElement(icon, { sx: { color: iconColor, fontSize: 18 } })}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  const fbData = fbStats?.today || fbStats?.total || {};
  const fbTotalLeads = parseInt(fbData?.total_leads) || 0;
  const fbPositiveLeads = parseInt(fbData?.positive_leads) || 0;
  const fbPendingLeads = parseInt(fbData?.pending_leads) || 0;
  const fbTrashLeads = parseInt(fbData?.trash_leads) || 0;
  const fbQualityScore = Math.round(parseFloat(fbStats?.total?.avg_quality_score) || 0);
  const fbPositiveRate = fbTotalLeads > 0 ? Math.round((fbPositiveLeads / fbTotalLeads) * 100) : 0;

  return (
    <Box>
      <Typography variant="caption" color="text.secondary">Dashboard</Typography>
      <Typography variant="h5" fontWeight={700} mb={2} sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>Today's Stats</Typography>

      {!isPartner() && (
        <Paper sx={{ p: 1.5, mb: 3, bgcolor: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 2 }}>
          <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
            Hi {userName}, you have{' '}
            <Link component="button" onClick={() => navigate('/conversions?status=pending')} sx={{ color: '#f97316', fontWeight: 600, textDecoration: 'none', cursor: 'pointer' }}>
              {stats?.pending || 0} conversion(s)
            </Link>
            {' '}pending approval.
          </Typography>
        </Paper>
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(3, 1fr)', md: isPartner() ? 'repeat(3, 1fr)' : 'repeat(6, 1fr)' }, gap: 1.5, mb: 3 }}>
        {!isPartner() && <StatCard title="Revenue" value={stats?.today?.revenue} change={revenueChange} prefix="€" icon={<Euro />} iconBgColor="#dbeafe" iconColor="#3b82f6" subValue={`Month: €${formatNumber(stats?.month?.revenue)}`} />}
        <StatCard title="Payout" value={stats?.today?.payout} change={payoutChange} prefix="€" icon={<Payments />} iconBgColor="#fef3c7" iconColor="#f59e0b" subValue={`Month: €${formatNumber(stats?.month?.payout)}`} />
        {!isPartner() && <StatCard title="Profit" value={stats?.today?.profit} change={profitChange} prefix="€" icon={<AccountBalance />} iconBgColor="#d1fae5" iconColor="#10b981" subValue={`Month: €${formatNumber(stats?.month?.profit)}`} />}
        {!isPartner() && <StatCard title="Margin" value={parseFloat(marginToday)} change={marginChange} prefix="" suffix="%" icon={<Percent />} iconBgColor="#e0e7ff" iconColor="#6366f1" subValue={`Month: ${marginMonth}%`} />}
        <StatCard title="Conversions" value={stats?.today?.conversions} change={conversionsChange} icon={<SwapHoriz />} iconBgColor="#fce7f3" iconColor="#ec4899" subValue={`Month: ${stats?.month?.conversions || 0}`} integer />
        <StatCard title="Clicks" value={stats?.today?.clicks} change={clicksChange} icon={<AdsClick />} iconBgColor="#f3e8ff" iconColor="#a855f7" subValue={`Month: ${stats?.month?.clicks || 0}`} integer />
      </Box>

      {/* Main Performance Chart */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
            <Typography variant="h6" fontWeight={600} sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>Performance</Typography>
               <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
  {!isPartner() && (
    <FormControl size="small" sx={{ minWidth: { xs: 100, sm: 140 } }}>
      <Select multiple value={chartMetrics} onChange={(e) => setChartMetrics(e.target.value.length > 0 ? e.target.value : ['revenue'])} renderValue={(selected) => selected.map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(', ')} sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
        <MenuItem value="revenue">Revenue</MenuItem>
        <MenuItem value="profit">Profit</MenuItem>
      </Select>
    </FormControl>
  )}
  <FormControl size="small" sx={{ minWidth: { xs: 140, sm: 200 } }}>
                <Select value={timeRange} onChange={(e) => setTimeRange(e.target.value)} sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                  <MenuItem value="today">Today vs Yesterday</MenuItem>
                  <MenuItem value="lastWeekday">Today vs Last {new Date().toLocaleDateString('en-US', { weekday: 'short' })}</MenuItem>
                  <MenuItem value="week">This Week vs Last</MenuItem>
                  <MenuItem value="month">This Month vs Last</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>
          <ResponsiveContainer width="100%" height={isMobile ? 200 : 280}>
            <AreaChart data={chartData}>
              <defs>
                <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="colorRevenuePrev" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#93c5fd" stopOpacity={0.5} />
                  <stop offset="95%" stopColor="#93c5fd" stopOpacity={0.1} />
                </linearGradient>
                <linearGradient id="colorProfit" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="colorProfitPrev" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#6ee7b7" stopOpacity={0.5} />
                  <stop offset="95%" stopColor="#6ee7b7" stopOpacity={0.1} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="label" stroke="#9ca3af" style={{ fontSize: isMobile ? '0.6rem' : '0.7rem' }} tickLine={false} interval={isMobile ? 4 : 2} />
              <YAxis stroke="#9ca3af" style={{ fontSize: '0.7rem' }} tickLine={false} axisLine={false} hide={isMobile} />
              <Tooltip content={renderTooltip(chartData, comparison.currentLabel, comparison.previousLabel)} />
              {chartMetrics.includes('revenue') && <Area type="monotone" dataKey="revenue" stroke="#3b82f6" strokeWidth={2} fill="url(#colorRevenue)" />}
              {chartMetrics.includes('revenue') && <Area type="monotone" dataKey="revenuePrev" stroke="#93c5fd" strokeWidth={1} fill="url(#colorRevenuePrev)" strokeDasharray="5 5" />}
              {chartMetrics.includes('profit') && <Area type="monotone" dataKey="profit" stroke="#10b981" strokeWidth={2} fill="url(#colorProfit)" />}
              {chartMetrics.includes('profit') && <Area type="monotone" dataKey="profitPrev" stroke="#6ee7b7" strokeWidth={1} fill="url(#colorProfitPrev)" strokeDasharray="5 5" />}
            </AreaChart>
          </ResponsiveContainer>
          <Box display="flex" justifyContent="center" gap={{ xs: 1, sm: 3 }} mt={1} flexWrap="wrap">
             {(isPartner() || chartMetrics.includes('revenue')) && (
  <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>
    {isPartner() ? 'Payout' : 'Revenue'}: <strong>€{(isPartner() ? comparison.current?.payout : comparison.current?.revenue)?.toFixed(2) || '0.00'}</strong> vs <strong>€{(isPartner() ? comparison.previous?.payout : comparison.previous?.revenue)?.toFixed(2) || '0.00'}</strong>
  </Typography>
)}
            {chartMetrics.includes('profit') && (
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>
                Profit: <strong>€{comparison.current?.profit?.toFixed(2) || '0.00'}</strong> vs <strong>€{comparison.previous?.profit?.toFixed(2) || '0.00'}</strong>
              </Typography>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Facebook Leads Section */}
      {isAdmin() && (
        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
            <Typography variant="h6" fontWeight={600} mb={2} sx={{ fontSize: { xs: '1rem', sm: '1.1rem' } }}>
              Facebook Leads - Today
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(3, 1fr)', sm: 'repeat(5, 1fr)' }, gap: 2 }}>
              <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: '#f8fafc', borderRadius: 2 }}>
                <Box display="flex" justifyContent="center" alignItems="center" gap={1} mb={0.5}>
                  <Typography variant="body2" color="text.secondary" fontWeight={500} sx={{ fontSize: '0.75rem' }}>Total Leads</Typography>
                  <Box sx={{ width: 24, height: 24, borderRadius: 1, bgcolor: '#dbeafe', display: { xs: 'none', sm: 'flex' }, alignItems: 'center', justifyContent: 'center' }}>
                    <People sx={{ color: '#3b82f6', fontSize: 14 }} />
                  </Box>
                </Box>
                <Typography variant="h5" fontWeight={700} sx={{ fontSize: '1.3rem', color: '#1e293b' }}>{fbTotalLeads}</Typography>
              </Box>
              <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: '#f8fafc', borderRadius: 2 }}>
                <Box display="flex" justifyContent="center" alignItems="center" gap={1} mb={0.5}>
                  <Typography variant="body2" color="text.secondary" fontWeight={500} sx={{ fontSize: '0.75rem' }}>Positive</Typography>
                  <Box sx={{ width: 24, height: 24, borderRadius: 1, bgcolor: '#d1fae5', display: { xs: 'none', sm: 'flex' }, alignItems: 'center', justifyContent: 'center' }}>
                    <CheckCircle sx={{ color: '#10b981', fontSize: 14 }} />
                  </Box>
                </Box>
                <Typography variant="h5" fontWeight={700} sx={{ fontSize: '1.3rem', color: '#10b981' }}>{fbPositiveLeads}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>{fbPositiveRate}%</Typography>
              </Box>
              <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: '#f8fafc', borderRadius: 2 }}>
                <Box display="flex" justifyContent="center" alignItems="center" gap={1} mb={0.5}>
                  <Typography variant="body2" color="text.secondary" fontWeight={500} sx={{ fontSize: '0.75rem' }}>Pending</Typography>
                  <Box sx={{ width: 24, height: 24, borderRadius: 1, bgcolor: '#fef3c7', display: { xs: 'none', sm: 'flex' }, alignItems: 'center', justifyContent: 'center' }}>
                    <HourglassFull sx={{ color: '#f59e0b', fontSize: 14 }} />
                  </Box>
                </Box>
                <Typography variant="h5" fontWeight={700} sx={{ fontSize: '1.3rem', color: '#f59e0b' }}>{fbPendingLeads}</Typography>
              </Box>
              <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: '#f8fafc', borderRadius: 2 }}>
                <Box display="flex" justifyContent="center" alignItems="center" gap={1} mb={0.5}>
                  <Typography variant="body2" color="text.secondary" fontWeight={500} sx={{ fontSize: '0.75rem' }}>Trash</Typography>
                  <Box sx={{ width: 24, height: 24, borderRadius: 1, bgcolor: '#fee2e2', display: { xs: 'none', sm: 'flex' }, alignItems: 'center', justifyContent: 'center' }}>
                    <Delete sx={{ color: '#ef4444', fontSize: 14 }} />
                  </Box>
                </Box>
                <Typography variant="h5" fontWeight={700} sx={{ fontSize: '1.3rem', color: '#ef4444' }}>{fbTrashLeads}</Typography>
              </Box>
              <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: '#f8fafc', borderRadius: 2 }}>
                <Box display="flex" justifyContent="center" alignItems="center" gap={1} mb={0.5}>
                  <Typography variant="body2" color="text.secondary" fontWeight={500} sx={{ fontSize: '0.75rem' }}>Quality</Typography>
                  <Box sx={{ width: 24, height: 24, borderRadius: 1, bgcolor: '#f3e8ff', display: { xs: 'none', sm: 'flex' }, alignItems: 'center', justifyContent: 'center' }}>
                    <Star sx={{ color: '#a855f7', fontSize: 14 }} />
                  </Box>
                </Box>
                <Typography variant="h5" fontWeight={700} sx={{ fontSize: '1.3rem', color: '#a855f7' }}>{fbQualityScore}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>su 100</Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Campaigns Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Typography variant="h6" fontWeight={600} mb={2} sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>Campaigns</Typography>
          <Box display="flex" gap={3} flexDirection={{ xs: 'column', lg: 'row' }}>
            {!isMobile && (
              <Box sx={{ flex: '1 1 40%' }}>
                {selectedCampaign ? (
                  <>
                    <Typography variant="subtitle1" fontWeight={600} mb={2} color="primary">{selectedCampaign.name}</Typography>
                    <ResponsiveContainer width="100%" height={250}>
                      <AreaChart data={campaignChartData}>
                        <defs>
                          <linearGradient id="colorCampRevenue" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                            <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis dataKey="label" stroke="#9ca3af" style={{ fontSize: '0.65rem' }} tickLine={false} interval={2} />
                        <YAxis stroke="#9ca3af" style={{ fontSize: '0.7rem' }} tickLine={false} axisLine={false} />
                        <Tooltip content={renderTooltip(campaignChartData, 'Today', 'Yesterday')} />
                        <Area type="monotone" dataKey="revenue" stroke="#3b82f6" strokeWidth={2} fill="url(#colorCampRevenue)" />
                        <Area type="monotone" dataKey="revenuePrev" stroke="#93c5fd" strokeWidth={1} fill="transparent" strokeDasharray="5 5" />
                      </AreaChart>
                    </ResponsiveContainer>
                    <Box display="flex" justifyContent="center" mt={1}>
                      <Typography variant="body2" color="text.secondary">
                      {isPartner() ? 'Payout' : 'Revenue'}: <strong>€{parseFloat(isPartner() ? selectedCampaign.payout_today : selectedCampaign.revenue_today || 0).toFixed(2)}</strong> vs <strong>€{parseFloat(isPartner() ? selectedCampaign.payout_yesterday : selectedCampaign.revenue_yesterday || 0).toFixed(2)}</strong>
                      </Typography>
                    </Box>
                  </>
                ) : (
                  <Box display="flex" justifyContent="center" alignItems="center" height={250}>
                    <Typography color="text.secondary">No campaigns with clicks today</Typography>
                  </Box>
                )}
              </Box>
            )}
            <Box sx={{ flex: { xs: '1 1 100%', lg: '1 1 60%' } }}>
              <Box display="flex" justifyContent="flex-end" mb={1}>
                <TextField size="small" placeholder="Search..." value={campaignSearch} onChange={(e) => setCampaignSearch(e.target.value)} sx={{ width: { xs: '100%', sm: 200 } }} InputProps={{ startAdornment: <Search sx={{ color: 'text.secondary', mr: 0.5, fontSize: 20 }} /> }} />
              </Box>
              <TableContainer sx={{ maxHeight: { xs: 300, lg: 350 } }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Campaign</TableCell>
                      <TableCell align="right">Clicks</TableCell>
                      <TableCell align="right">CV</TableCell>
                      {!isPartner() && <TableCell align="right">Revenue</TableCell>}
                      <TableCell align="right">Payout</TableCell>
                      {!isPartner() && <TableCell align="right">Profit</TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {campaignStats.filter(c => c.name.toLowerCase().includes(campaignSearch.toLowerCase())).map((campaign) => (
                      <TableRow key={campaign.campaign_id} hover onClick={() => handleCampaignClick(campaign)} sx={{ cursor: 'pointer', bgcolor: selectedCampaign?.campaign_id === campaign.campaign_id ? '#eff6ff' : 'inherit' }}>
                        <TableCell>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: '#2563eb', 
                              fontWeight: selectedCampaign?.campaign_id === campaign.campaign_id ? 600 : 400, 
                              fontSize: { xs: '0.75rem', sm: '0.875rem' },
                              cursor: 'pointer',
                              '&:hover': { textDecoration: 'underline' }
                            }}
                            onClick={(e) => { e.stopPropagation(); navigate(`/campaigns/${campaign.campaign_id}`); }}
                          >
                            {campaign.name}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>{campaign.clicks_today || 0}</TableCell>
                        <TableCell align="right" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>{campaign.conversions_today || 0}</TableCell>
                        {!isPartner() && <TableCell align="right" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>€{parseFloat(campaign.revenue_today || 0).toFixed(2)}</TableCell>}
                        <TableCell align="right" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>€{parseFloat(campaign.payout_today || 0).toFixed(2)}</TableCell>
                        {!isPartner() && <TableCell align="right" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, color: (parseFloat(campaign.revenue_today || 0) - parseFloat(campaign.payout_today || 0)) >= 0 ? '#10b981' : '#ef4444' }}>
                          €{(parseFloat(campaign.revenue_today || 0) - parseFloat(campaign.payout_today || 0)).toFixed(2)}
                        </TableCell>}
                      </TableRow>
                    ))}
                    {campaignStats.length === 0 && (
                      <TableRow><TableCell colSpan={isPartner() ? 4 : 6} align="center"><Typography color="text.secondary" variant="body2">No campaigns with clicks today</Typography></TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box display="flex" justifyContent="flex-end" mt={1}>
                <Typography variant="caption" color="text.secondary">{campaignStats.length} Total</Typography>
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Partners Section */}
      {!isPartner() && (
        <Card>
          <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
            <Typography variant="h6" fontWeight={600} mb={2} sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>Partners</Typography>
            <Box display="flex" gap={3} flexDirection={{ xs: 'column', lg: 'row' }}>
              {!isMobile && (
                <Box sx={{ flex: '1 1 40%' }}>
                  {selectedPartner ? (
                    <>
                      <Typography variant="subtitle1" fontWeight={600} mb={2} color="primary">{selectedPartner.name}</Typography>
                      <ResponsiveContainer width="100%" height={250}>
                        <AreaChart data={partnerChartData}>
                          <defs>
                            <linearGradient id="colorPartRevenue" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                              <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                          <XAxis dataKey="label" stroke="#9ca3af" style={{ fontSize: '0.65rem' }} tickLine={false} interval={2} />
                          <YAxis stroke="#9ca3af" style={{ fontSize: '0.7rem' }} tickLine={false} axisLine={false} />
                          <Tooltip content={renderTooltip(partnerChartData, 'Today', 'Yesterday')} />
                          <Area type="monotone" dataKey="revenue" stroke="#3b82f6" strokeWidth={2} fill="url(#colorPartRevenue)" />
                          <Area type="monotone" dataKey="revenuePrev" stroke="#93c5fd" strokeWidth={1} fill="transparent" strokeDasharray="5 5" />
                        </AreaChart>
                      </ResponsiveContainer>
                      <Box display="flex" justifyContent="center" mt={1}>
                        <Typography variant="body2" color="text.secondary">
                          Revenue: <strong>€{parseFloat(selectedPartner.revenue_today || 0).toFixed(2)}</strong> vs <strong>€{parseFloat(selectedPartner.revenue_yesterday || 0).toFixed(2)}</strong>
                        </Typography>
                      </Box>
                    </>
                  ) : (
                    <Box display="flex" justifyContent="center" alignItems="center" height={250}>
                      <Typography color="text.secondary">No partners with clicks today</Typography>
                    </Box>
                  )}
                </Box>
              )}
              <Box sx={{ flex: { xs: '1 1 100%', lg: '1 1 60%' } }}>
                <Box display="flex" justifyContent="flex-end" mb={1}>
                  <TextField size="small" placeholder="Search..." value={partnerSearch} onChange={(e) => setPartnerSearch(e.target.value)} sx={{ width: { xs: '100%', sm: 200 } }} InputProps={{ startAdornment: <Search sx={{ color: 'text.secondary', mr: 0.5, fontSize: 20 }} /> }} />
                </Box>
                <TableContainer sx={{ maxHeight: { xs: 300, lg: 350 } }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Partner</TableCell>
                        <TableCell align="right">Clicks</TableCell>
                        <TableCell align="right">CV</TableCell>
                        <TableCell align="right">Revenue</TableCell>
                        <TableCell align="right">Payout</TableCell>
                        <TableCell align="right">Profit</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {partnerStats.filter(p => p.name.toLowerCase().includes(partnerSearch.toLowerCase())).map((partner) => (
                        <TableRow key={partner.partner_id} hover onClick={() => handlePartnerClick(partner)} sx={{ cursor: 'pointer', bgcolor: selectedPartner?.partner_id === partner.partner_id ? '#eff6ff' : 'inherit' }}>
                          <TableCell>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                color: '#2563eb', 
                                fontWeight: selectedPartner?.partner_id === partner.partner_id ? 600 : 400, 
                                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                cursor: 'pointer',
                                '&:hover': { textDecoration: 'underline' }
                              }}
                              onClick={(e) => { e.stopPropagation(); navigate(`/partners/${partner.partner_id}`); }}
                            >
                              {partner.name}
                            </Typography>
                          </TableCell>
                          <TableCell align="right" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>{partner.clicks_today || 0}</TableCell>
                          <TableCell align="right" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>{partner.conversions_today || 0}</TableCell>
                          <TableCell align="right" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>€{parseFloat(partner.revenue_today || 0).toFixed(2)}</TableCell>
                          <TableCell align="right" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>€{parseFloat(partner.payout_today || 0).toFixed(2)}</TableCell>
                          <TableCell align="right" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, color: (parseFloat(partner.revenue_today || 0) - parseFloat(partner.payout_today || 0)) >= 0 ? '#10b981' : '#ef4444' }}>
                            €{(parseFloat(partner.revenue_today || 0) - parseFloat(partner.payout_today || 0)).toFixed(2)}
                          </TableCell>
                        </TableRow>
                      ))}
                      {partnerStats.length === 0 && (
                        <TableRow><TableCell colSpan={6} align="center"><Typography color="text.secondary" variant="body2">No partners with clicks today</Typography></TableCell></TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Box display="flex" justifyContent="flex-end" mt={1}>
                  <Typography variant="caption" color="text.secondary">{partnerStats.length} Total</Typography>
                </Box>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}

export default Dashboard;
